import { useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { UseQueryResult } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";

import { useLocationName } from "@/api/locations";
import { useNeededFor, useNeeds, type Need, type Peg } from "@/api/planningRun";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDate, formatNumber } from "@/utils/formatters";

import { OrderKind } from "./OrderKind";
import { OrderStatus } from "./OrderStatus";

type OrderRef = { run: number; orderId: number };

export function NeededFor({ run, orderId }: OrderRef) {
  const { t } = useTranslation();
  return (
    <PegTree
      query={useNeededFor(run, orderId)}
      next={(peg) => peg.neededFor}
      title={t("planningReview.neededFor", "Needed for")}
      failed={t(
        "planningReview.neededForFailed",
        "Could not load what this order is needed for.",
      )}
      empty={t(
        "planningReview.independentDemandOnly",
        "Nothing else draws on this order: it covers independent demand only.",
      )}
      expandLabel={(item) =>
        t("planningReview.showNeededFor", "Show what {{item}} is needed for", {
          item,
        })
      }
      drawLabel={(quantity, date) =>
        t("planningReview.draws", "Draws {{quantity}} on {{date}}", {
          quantity,
          date,
        })
      }
    />
  );
}

export function Needs({ run, orderId }: OrderRef) {
  const { t } = useTranslation();
  return (
    <PegTree
      query={useNeeds(run, orderId)}
      next={(need) => need.needs}
      title={t("planningReview.needs", "Needs")}
      failed={t(
        "planningReview.needsFailed",
        "Could not load what this order needs.",
      )}
      empty={t(
        "planningReview.needsNothing",
        "No order supplies what this order draws.",
      )}
      expandLabel={(item) =>
        t("planningReview.showNeeds", "Show what {{item}} needs", { item })
      }
      drawLabel={(quantity, date) =>
        t("planningReview.covers", "Covers {{quantity}} drawn on {{date}}", {
          quantity,
          date,
        })
      }
    />
  );
}

type Row = Peg | Need;

interface TreeProps<T extends Row> {
  next: (row: T) => T[];
  expandLabel: (item: string) => string;
  drawLabel: (quantity: string, date: string) => string;
}

function PegTree<T extends Row>({
  query,
  title,
  failed,
  empty,
  ...tree
}: TreeProps<T> & {
  query: UseQueryResult<T[]>;
  title: string;
  failed: string;
  empty: string;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : query.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {failed}
        </p>
      ) : query.data?.length ? (
        <PegList rows={query.data} {...tree} />
      ) : (
        <p className="text-muted-foreground text-sm">{empty}</p>
      )}
    </section>
  );
}

function PegList<T extends Row>({
  rows,
  ...tree
}: TreeProps<T> & { rows: T[] }) {
  return (
    <ul className="space-y-1">
      {rows.map((row) => (
        // ponytail: an order drawing twice on one node on one day repeats its key.
        <PegRow
          key={`${row.plannedOrder}-${row.drawDate}`}
          row={row}
          {...tree}
        />
      ))}
    </ul>
  );
}

function PegRow<T extends Row>({ row, ...tree }: TreeProps<T> & { row: T }) {
  const locationName = useLocationName();
  const [open, setOpen] = useState(false);
  const id = row.plannedOrder;
  const children = tree.next(row);
  const label = `${row.itemSku} · ${row.itemName} · ${locationName(row.location)}`;

  return (
    <li>
      {/* The link's ::after covers the row, so a click anywhere selects the
          order; only controls lifted with `relative` sit above it. */}
      <div className="relative flex items-center gap-2 text-sm">
        {children.length > 0 ? (
          <Button
            size="icon"
            variant="ghost"
            className="relative z-10 size-7 shrink-0"
            aria-expanded={open}
            aria-label={tree.expandLabel(row.itemSku)}
            onClick={() => setOpen(!open)}
          >
            <ChevronRight className={cn(open && "rotate-90")} />
          </Button>
        ) : (
          <span className="size-7 shrink-0" />
        )}
        <div className="min-w-0 grow">
          <Link
            to={`?order=${id}`}
            className="block truncate font-medium after:absolute after:inset-0 hover:underline"
          >
            {label}
          </Link>
          <span className="text-muted-foreground flex items-center gap-1 text-xs tabular-nums">
            <OrderKind flavor={row.flavor} /> ·{" "}
            {tree.drawLabel(
              formatNumber(row.drawQuantity, { maximumFractionDigits: 4 }),
              formatDate(row.drawDate),
            )}
          </span>
        </div>
        <OrderStatus status={row.status} />
      </div>
      {open && (
        <div className="ml-3.5 border-l pl-3">
          <PegList rows={children} {...tree} />
        </div>
      )}
    </li>
  );
}
